/**
 * Drawn-signature capture pad (canvas + pointer events).
 *
 * Used for material-PO delivery sign-off. Exports the drawing as a PNG blob
 * via onSave(blob). Self-contained — no external drawing library.
 */
import { useEffect, useRef, useState } from "react";
import { buttonNeutralSmCls } from "../styles/ui";

const PAD_HEIGHT = 160;

export default function SignaturePad({ onSave, saving = false, disabled = false }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Size the backing store for the device pixel ratio so strokes stay crisp.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    canvas.width = width * dpr;
    canvas.height = PAD_HEIGHT * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#18181b"; // zinc-900 — ink on the white pad
  }, []);

  function pos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handleDown(e) {
    if (disabled || saving) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Dot for a single tap
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    setHasInk(true);
  }

  function handleMove(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function handleUp() {
    drawingRef.current = false;
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setHasInk(false);
  }

  function save() {
    if (!hasInk) return;
    canvasRef.current.toBlob((blob) => {
      if (blob) onSave(blob);
    }, "image/png");
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{ height: PAD_HEIGHT, touchAction: "none" }}
        className="w-full bg-surface border border-zinc-300 rounded-lg cursor-crosshair"
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
      />
      <div className="flex items-center gap-2 mt-2">
        <button type="button" onClick={clear} disabled={!hasInk || saving} className={`${buttonNeutralSmCls} disabled:opacity-50`}>
          Clear
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!hasInk || saving || disabled}
          className="text-xs bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
        >
          {saving ? "Saving…" : "Save Signature"}
        </button>
        {!hasInk && <span className="text-xs text-ink-muted">Sign above</span>}
      </div>
    </div>
  );
}
